import {api, RequestParam} from "@/shared/api";
import * as Input from "@/shared/utils/input";

import {showPdfPreview} from "@/features/pdfPreview/showPdfPreview";
import {TimeAccountTable} from "@/features/timeaccounts/timeAccountTable";
import {
	ValidationAddEventDetail,
	ValidationRecordEventDetail,
	ValidationsCollapseComponent
} from "@/features/validations/validationsCollapseComponent";
import {ButtonRadioGroup} from "@/shared/ui/buttonRadioGroup";
import {WorkingTimeDialog} from "@/features/workingtime/workingTimeDialog";
import {ProjectTimeDialog} from "@/features/projecttime/projectTimeDialog";
import {getWorkingTimeTypesForAddAbsence, getWorkingTimeTypesForAddWorkingTime} from "@/shared/securityHelper";
import {
	getStartEndTimeForMissingRecordingValidation,
	resetRecordingValidationLinkIconLoading,
	setRecordingValidationLinkIconLoading,
} from "@/shared/validations/recordingValidationUtil";
import * as TimeVacationAccountInfo from "@/features/timeaccounts/timeVacationAccountInfo";
import {BrowserStorageSettings} from "@/shared/browserStorageSettings";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import {
	formatDate,
	getCurrentISOOffset,
	getISODate,
	parseDateInput,
	parseISODate,
	parseISOPlainDateTime,
	Temporal
} from "@/shared/utils/datefunctions";
import {
	CreateExportResponse,
	CustomFieldDefinition,
	IntegrationDetails,
	OvertimeRule,
	StartEndTime,
	TimeAccountDashboardBalanceMode,
	TimeAccountVacationData,
	TimeAccountViewType,
	User,
	ValidationResult,
	ValidationResultRecordType,
	WorkingTimeType
} from "@/shared/timr";
import {DateTimeChangeableByUserDayRangeMode} from "@/shared/timrTypes";

export interface TimeAccountDashboardConfig {
	users: User[];
	usersWithInitializedTimeAccount: User[];
	defaultUserId: number;
	workingTimeTypes: WorkingTimeType[];
	isGroupLeaderForThemself: boolean;
	customFieldDefinitionsWorkingTime: CustomFieldDefinition[];
	defaultStatusNumberWorkingTime: number;
	allowedUserIds: number[];
	customFieldDefinitionsProjectTime: CustomFieldDefinition[];
	defaultStatusNumberProjectTime: number;
	allowedToChangeBillable: boolean;
	lastTimeAccountBalanceDate: string;
	nextTimeAccountBalanceDate: string;
	showOvertimesColumn: boolean;
	showTravellingTimesColumn: boolean;
	overtimeRules: OvertimeRule[];
	timeAccountViewType: TimeAccountViewType;
	showFlexTimeIndicatorColumn: boolean;
	workingTimeDateTimeChangeableByUserDayRangeUntil: string | null;
	workingTimeDateTimeChangeableByUserDayRangeMode: DateTimeChangeableByUserDayRangeMode;
	projectTimeDateTimeChangeableByUserDayRangeUntil: string | null;
	projectTimeDateTimeChangeableByUserDayRangeMode: DateTimeChangeableByUserDayRangeMode;
	timeAccountExportIntegrations: IntegrationDetails[];
}

export abstract class TimeAccountBase {

	public static GROUP_TIME_ACCOUNT = "timeAccountGroup";
	protected static OVERTIME_RULE_ALL_OVERTIME_ACCOUNTS_ID = -2;
	protected timeAccountTable: TimeAccountTable;
	protected updateCurrentTimeAccountDataRequest: JQuery.jqXHR;
	protected updateValidateWorkingTimesRequest: JQuery.jqXHR;
	protected balanceModeControl: ButtonRadioGroup<TimeAccountDashboardBalanceMode>;
	private previewDialog: BootstrapModal.Modal;
	protected validationsCollapseComponent: ValidationsCollapseComponent;
	protected config: TimeAccountDashboardConfig;

	private workingTimeDialog: WorkingTimeDialog;
	private projectTimeDialog: ProjectTimeDialog;

	// save the last used group id, user id and show user flag
	// to be able to refresh the validations when a new working time is created
	private groupId: number;
	private userId: number;
	private showUsers: boolean;

	constructor(config: TimeAccountDashboardConfig) {
		this.config = config;
		$("#workDurationCurrentPeriodPreview").on("click", () => {
			if ($("#workDurationCurrentPeriodPreview").data("isInitialized")) {
				this.showPreviewDialog();
			} else {
				BootstrapModal.info({
					title: "report.timeAccountDashboard.userNotInitialized.title",
					message: "report.timeAccountDashboard.userNotInitialized.message"
				});
			}
		});
		this.initBalanceModeControl();
		this.initPreviewDialog();
		this.initValidationListeners();
		this.initDialogs();
	}

	protected initBalanceModeControl() {
		this.balanceModeControl = ButtonRadioGroup.typed<TimeAccountDashboardBalanceMode>({
			value: "YESTERDAY",
			button: document.querySelector(".timeAccountDashboardBalanceModeYesterdayButton")
		}, {
			value: "TODAY",
			button: document.querySelector(".timeAccountDashboardBalanceModeTodayButton")
		}).onValueChanged(mode => this.onBalanceModeChanged(mode));
		this.balanceModeControl.setValue(BrowserStorageSettings.getTimeAccountDashboardBalanceMode(TimeAccountBase.GROUP_TIME_ACCOUNT) ?? "TODAY");
	}

	protected initValidationListeners() {
		// init validations web component and add type
		this.validationsCollapseComponent = document.getElementById("validationsCollapse") as ValidationsCollapseComponent;

		this.validationsCollapseComponent.addEventListener(ValidationsCollapseComponent.EVENT_ADD_WORKING_TIME_CLICKED, e => {
			const customEvent = e as CustomEvent<ValidationAddEventDetail>;
			void this.addWorkingTime(customEvent.detail.validationId, customEvent.detail.userId, customEvent.detail.displayDate);
		});
		this.validationsCollapseComponent.addEventListener(ValidationsCollapseComponent.EVENT_ADD_ABSENCE_CLICKED, e => {
			const customEvent = e as CustomEvent<ValidationAddEventDetail>;
			void this.addAbsence(customEvent.detail.validationId, customEvent.detail.userId, customEvent.detail.displayDate);
		});
		this.validationsCollapseComponent.addEventListener(ValidationsCollapseComponent.EVENT_RECORD_CLICKED, e => {
			const customEvent = e as CustomEvent<ValidationRecordEventDetail>;
			this.openDialog(customEvent.detail.recordType, customEvent.detail.id, customEvent.detail.link);
		});
		this.validationsCollapseComponent.addEventListener(ValidationsCollapseComponent.EVENT_RECORD_REF_CLICKED, e => {
			const customEvent = e as CustomEvent<ValidationRecordEventDetail>;
			this.openDialog(customEvent.detail.recordType, customEvent.detail.id, customEvent.detail.link);
		});
	}

	protected initDialogs() {
		this.workingTimeDialog = new WorkingTimeDialog({
			dialogId: "#workingTimeDialog",
			workingTimeTypes: this.config.workingTimeTypes,
			users: this.config.users,
			userSelectionEnabled: true,
			customFieldDefinitions: this.config.customFieldDefinitionsWorkingTime,
			defaultStatusNumber: this.config.defaultStatusNumberWorkingTime,
			allowedUserIds: this.config.allowedUserIds,
			isGroupLeaderForThemself: this.config.isGroupLeaderForThemself,
			dateTimeChangeableByUserDayRangeUntil: parseISODate(this.config.workingTimeDateTimeChangeableByUserDayRangeUntil),
			dateTimeChangeableByUserDayRangeMode: this.config.workingTimeDateTimeChangeableByUserDayRangeMode
		});
		$(this.workingTimeDialog)
			.on(WorkingTimeDialog.EVENT_RECORD_CREATED, () => {
				this.validateWorkingTimes(this.groupId, this.userId, this.showUsers);
			})
			.on(WorkingTimeDialog.EVENT_REQUEST_CREATED, () => {
				this.validateWorkingTimes(this.groupId, this.userId, this.showUsers);
			});
		this.projectTimeDialog = new ProjectTimeDialog({
			dialogId: "#projectTimeDialog",
			users: this.config.users,
			userSelectionEnabled: true,
			customFieldDefinitions: this.config.customFieldDefinitionsProjectTime,
			defaultStatusNumber: this.config.defaultStatusNumberProjectTime,
			allowedUserIds: this.config.allowedUserIds,
			isGroupLeaderForThemself: this.config.isGroupLeaderForThemself,
			dateTimeChangeableByUserDayRangeUntil: parseISODate(this.config.projectTimeDateTimeChangeableByUserDayRangeUntil),
			dateTimeChangeableByUserDayRangeMode: this.config.projectTimeDateTimeChangeableByUserDayRangeMode
		});
	}

	protected abstract getPreviewTimeSheetUserId(): number;

	protected showPreviewDialog() {
		this.previewDialog.show();
	}

	private initPreviewDialog() {
		const previewDate = $("#previewTimeAccountDialogDate");
		this.previewDialog = new BootstrapModal.Modal({
			title: "report.timeAccountDashboard.preview.dialog.header",
			message: $("#previewTimeAccountDialog"),
			buttons: [{
				text: "dialog.create.report",
				css: "btn-primary",
				action: (modal, button) => {
					this.previewTimeSheet(null, this.getPreviewTimeSheetUserId(), parseDateInput(previewDate.val() as string), null, true)
						.done(() => modal.close())
						.fail(() => this.previewDialog.resetLoadingButtons());
				}
			}, {
				text: "dialog.cancel",
			}]
		});

		$("#setPreviewTimeAccountDateToLastBalance").click(() => {
			const lastTimeAccountBalanceDate = Input.getLastMonthEnd();
			previewDate.val(formatDate(lastTimeAccountBalanceDate));
		});

		$("#setPreviewTimeAccountDateToDayBeforeToday").click(() => {
			const mode = BrowserStorageSettings.getTimeAccountDashboardBalanceMode(TimeAccountBase.GROUP_TIME_ACCOUNT);
			this.setPreviewTimeSheetDialogDate(mode);
		});
		const mode = BrowserStorageSettings.getTimeAccountDashboardBalanceMode(TimeAccountBase.GROUP_TIME_ACCOUNT);
		this.setPreviewTimeSheetDialogDate(mode);
		this.updateForwardPreviewDateButtonTitle(mode);
	}

	/**
	 * Sets the default value of the date field in the preview time sheet dialog
	 * Based on timeAccountDashboardBalanceMode this will be the date of yesterday or today (at start of the day)
	 *
	 * @private
	 */
	protected setPreviewTimeSheetDialogDate(mode: TimeAccountDashboardBalanceMode) {
		const date = mode === "YESTERDAY"
			? Temporal.Now.plainDateISO().subtract({days: 1})
			: Temporal.Now.plainDateISO();
		$("#previewTimeAccountDialogDate").val(formatDate(date));
	}

	/**
	 * Updates the title/tooltip of the arrow-right button to update the date in the field (today or yesterday)
	 *
	 * @param mode
	 * @private
	 */
	protected updateForwardPreviewDateButtonTitle(mode: TimeAccountDashboardBalanceMode) {
		const title = mode === "TODAY" ? messages["timeAccount.preview.setToday"] : messages["timeAccount.preview.setToDayBeforeToday"];
		$("#setPreviewTimeAccountDateToDayBeforeToday").prop("title", title);
	}

	/**
	 * Shows a time sheet preview for a given selection
	 * @param groupId id of the group if selected, otherwise null (if corporate features allowed)
	 * @param userId id of the user if selected, otherwise null
	 * @param date selected future date for preview
	 * @param activeUsersOnly exports only the users timeSheet if true
	 * @return {JQueryPromise<any>} To recognize if the download was successful
	 */
	protected previewTimeSheet(groupId: number, userId: number, date?: Temporal.PlainDate, description?: string, activeUsersOnly = false): JQuery.jqXHR<CreateExportResponse> {
		return api("timeaccounts/previewTimeSheet.json")
			.get({
				groupId: groupId,
				userId: userId,
				date: getISODate(date),
				timeZone: getCurrentISOOffset(),
				previewTimeSheetActiveUsersOnly: activeUsersOnly,
				description: description
			})
			.done(data => {
				if (data.exportJobId) {
					showPdfPreview(messages["report.timeAccountDashboard.preview.dialog.header"], data.exportJobId, messages["downloadStatus.loadingMessage.timeAccount"]);
				}
			});
	}

	/**
	 * Shows an exit time sheet preview for a given selection
	 * @return {JQueryPromise<any>} To recognize if the download was successful
	 * @param formData
	 */
	protected previewExitTimeSheet(formData: RequestParam[]): JQuery.jqXHR<CreateExportResponse> {
		return api("timeaccounts/previewExitTimeSheet.json")
			.get(formData)
			.done(data => {
				if (data.exportJobId) {
					showPdfPreview(messages["report.timeAccountDashboard.preview.dialog.header"], data.exportJobId, messages["downloadStatus.loadingMessage.timeAccount"]);
				}
			});
	}

	protected showTimeAccountData(timeAccountData: TimeAccountVacationData) {
		TimeVacationAccountInfo.setTimeAccountVacationFieldValues($("#timeVacationAccountInfoPanel"), timeAccountData, true);

		const isInitialized = timeAccountData?.isInitialized || false;
		const workDurationCurrentPeriodPreview = $("#workDurationCurrentPeriodPreview");
		workDurationCurrentPeriodPreview.data("isInitialized", isInitialized);
		this.toggleMenuLoadingButton(isInitialized);
		workDurationCurrentPeriodPreview.data("lastTimeAccountBalanceDate", timeAccountData?.lastTimeAccountBalanceDate);
		workDurationCurrentPeriodPreview.prop("disabled", timeAccountData == null);
	}

	protected toggleMenuLoadingButton(setButtonLoading: boolean) {
		$("#workDurationCurrentPeriodPreview").toggle(setButtonLoading);
		$("#workDurationCurrentPeriodPreviewLoadingIndicator").toggle(!setButtonLoading);
		$("#contextChangeConfigurationButton").toggle(setButtonLoading);
		$("#contextChangeConfigurationButtonLoadingIndicator").toggle(!setButtonLoading);
		$("#contextCreateExitBookingButton").toggle(setButtonLoading);
		$("#contextCreateExitBookingButtonLoadingIndicator").toggle(!setButtonLoading);
		$("#contextCreateReEntryBookingButton").toggle(setButtonLoading);
		$("#contextCreateReEntryBookingButtonLoadingIndicator").toggle(!setButtonLoading);
	}

	protected getCurrentTimeAccountVacationData(mode: TimeAccountDashboardBalanceMode, userId: number): JQuery.jqXHR<TimeAccountVacationData> {
		TimeVacationAccountInfo.toggleTimeVacationAccountInfoPanelLoadingIndicator(true);

		$("#workDurationCurrentPeriodPreview").data("isInitialized", false);
		this.updateCurrentTimeAccountDataRequest = api("timeaccounts/getCurrentTimeAccountVacationData.json")
			.get({
				userId: userId,
				timeAccountDashboardBalanceMode: mode,
				loadOvertimes: true,
				loadTravellingTimes: true,
				timeZone: getCurrentISOOffset()
			})
			.done((timeAccountData: TimeAccountVacationData) => {
				this.showTimeAccountData(timeAccountData);
			})
			.always(() => {
				TimeVacationAccountInfo.toggleTimeVacationAccountInfoPanelLoadingIndicator(false);
			});
		return this.updateCurrentTimeAccountDataRequest;
	}

	protected abstract onBalanceModeChanged(mode: TimeAccountDashboardBalanceMode): void;

	protected validateWorkingTimes(groupId: number, userId: number, showUser: boolean) {
		this.validationsCollapseComponent.toggleLoadingOverlay(true);
		const validationsLoadingIndicator = $(".validationsLoadingIndicator");
		validationsLoadingIndicator.show();

		this.groupId = groupId;
		this.userId = userId;
		this.showUsers = showUser;

		this.updateValidateWorkingTimesRequest?.abort();
		this.updateValidateWorkingTimesRequest = api("timeaccounts/validateRecordings.json")
			.get({
				userId: userId,
				groupId: groupId,
				timeZone: getCurrentISOOffset(),
				timeAccountViewType: this.config.timeAccountViewType
			})
			.done((response: { validationResults: ValidationResult[], totalCount: number }) => {
				this.validationsCollapseComponent.content = {
					validations: response.validationResults,
					isAddWorkingTimeButtonActive: getWorkingTimeTypesForAddWorkingTime(this.config.workingTimeTypes).length > 0,
					isAddAbsenceButtonActive: getWorkingTimeTypesForAddAbsence(this.config.workingTimeTypes).length > 0,
					validationTotalCount: response.totalCount
				};
			})
			.always(() => {
				validationsLoadingIndicator.hide();
				this.validationsCollapseComponent.toggleLoadingOverlay(false);
			});
	}

	protected getWorkingTimeById(workingTimeRecordId: number, link: JQuery) {
		setRecordingValidationLinkIconLoading(link);
		api("reports/getWorkingTime.json")
			.get({workingTimeId: workingTimeRecordId})
			.done(data => {
				this.workingTimeDialog.show(data);
			})
			.always(() => resetRecordingValidationLinkIconLoading(link));
	}

	protected getProjectTimeById(projectTimeRecordId: number, link: JQuery) {
		setRecordingValidationLinkIconLoading(link);
		api("reports/getProjectTime.json")
			.get({projectTimeId: projectTimeRecordId})
			.done(data => {
				this.projectTimeDialog.show(data);
			})
			.always(() => resetRecordingValidationLinkIconLoading(link));
	}

	private openDialog(recordType: ValidationResultRecordType, id: number, link: JQuery) {
		if (recordType === "workingTime") {
			this.getWorkingTimeById(id, link);
		} else if (recordType === "projectTime") {
			this.getProjectTimeById(id, link);
		}
	}

	private async addWorkingTime(validationId: number, userId: number, displayDate: string) {
		// Error handling happens inside api() -> no try/catch needed
		const startEndDateTime: StartEndTime = await getStartEndTimeForMissingRecordingValidation(validationId, userId, displayDate);
		this.workingTimeDialog.create(
			{userId: userId},
			false,
			getWorkingTimeTypesForAddWorkingTime(this.config.workingTimeTypes)
		);
		const start = parseISOPlainDateTime(startEndDateTime.start)?.toZonedDateTime(Temporal.Now.timeZoneId()) ?? null;
		const end = parseISOPlainDateTime(startEndDateTime.end)?.toZonedDateTime(Temporal.Now.timeZoneId()) ?? null;
		this.workingTimeDialog.setStartEndTime(start, end);
	}

	private async addAbsence(validationId: number, userId: number, displayDate: string) {
		// Error handling happens inside api() -> no try/catch needed
		const startEndDateTime: StartEndTime = await getStartEndTimeForMissingRecordingValidation(validationId, userId, displayDate);
		this.workingTimeDialog.create(
			{userId: userId},
			false,
			getWorkingTimeTypesForAddAbsence(this.config.workingTimeTypes),
		);
		const start = parseISOPlainDateTime(startEndDateTime.start)?.toZonedDateTime(Temporal.Now.timeZoneId()) ?? null;
		const end = parseISOPlainDateTime(startEndDateTime.end)?.toZonedDateTime(Temporal.Now.timeZoneId()) ?? null;
		this.workingTimeDialog.setStartEndTime(start, end);
	}
}
